// The 48-hour offline session (TODO §14.6).
//
// What actually breaks on a plane is narrower than it looks. Firebase persists its session in
// its own IndexedDB and `onAuthStateChanged` reports the cached user offline, so `status`
// stays `"in"` and the §9.4 export gate already passes. What fails is **token refresh**:
// after ~1h `getFreshIdToken()` cannot reach the network and returns `null`, which breaks the
// Gridd Library (§8.3.1) and anything else token-gated — not the user's own decks.
//
// So the rule this module exists to express: **the user's own work is never gated offline;
// someone else's premium content is, on a stated clock.** It records when a token was last
// genuinely minted against the network, and answers whether that was recent enough to keep
// trusting a cached entitlement.
//
// §14.6.8 Clock tampering is explicitly not defended against. The threat model is a
// consultant on a plane, and the app is already on their machine. Noted, and moved on.

// §14.6.2 The window, with the reasoning attached because the number is the feature: the
// longest scheduled commercial flights are ~19h, and 48h covers a long-haul plus a layover
// and a night at the other end without the user being logged out on arrival. 24h is the
// floor — anything under that fails the case this exists for. It matches
// `DEFAULT_OFFLINE_ENTITLEMENT_MS` in `@gridd/library-embed/data` by design: "signed in
// offline" and "entitled offline" must not expire at different moments and leave the user
// signed in but locked out.
export const GRACE_MS = 48 * 60 * 60 * 1000;

// How recently a token must have been issued for us to conclude the mint actually hit the
// network. Firebase hands back its cached token until shortly before expiry, so an `iat` older
// than this came from cache and proves nothing about connectivity.
const FRESH_MINT_MS = 5 * 60 * 1000;

const STORAGE_KEY = "slidecraft:auth:last-verified";
// Several accounts on one machine is normal (a personal and a work login); more than a
// handful is not, and the record is pruned so it cannot grow without bound.
const MAX_TRACKED_UIDS = 8;

type VerificationRecord = Record<string, number>;

function readAll(): VerificationRecord {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const out: VerificationRecord = {};
    for (const [uid, at] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof at === "number" && Number.isFinite(at)) out[uid] = at;
    }
    return out;
  } catch {
    // Corrupt JSON or storage disabled (private mode). "Never verified" is the safe answer:
    // it closes the offline window early rather than extending it on a guess.
    return {};
  }
}

function writeAll(record: VerificationRecord): void {
  if (typeof window === "undefined") return;
  try {
    const entries = Object.entries(record)
      .sort((a, b) => b[1] - a[1])
      .slice(0, MAX_TRACKED_UIDS);
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(Object.fromEntries(entries)),
    );
  } catch {
    // Quota or private mode — the in-memory session still works for this run; only the
    // "offline since…" line and the grace window lose their memory across a reload.
  }
}

/** The `iat` claim of a Firebase ID token, in ms, or null if it cannot be read. */
function issuedAt(token: string): number | null {
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    // Base64url → base64, then the standard decode. A malformed token is not worth throwing
    // for: it just means we learn nothing about when it was minted.
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as { iat?: number };
    return typeof claims.iat === "number" ? claims.iat * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * §14.6.1 Record a token mint that actually reached the network.
 *
 * Called on every successful mint. A token whose `iat` is old came out of Firebase's cache
 * and says nothing about connectivity, so it is ignored — recording it would silently extend
 * the offline window forever and the 48h clock would never mean anything.
 *
 * Returns the stored `lastVerifiedAt` for the uid (unchanged when the token was cached).
 */
export function noteTokenMinted(
  uid: string,
  token: string,
  now: number = Date.now(),
): number | null {
  const iat = issuedAt(token);
  const record = readAll();
  const previous = record[uid] ?? null;
  if (iat === null || now - iat > FRESH_MINT_MS) return previous;
  // `now`, not `iat`: this is when *we* saw the network, and a device clock behind the
  // server's would otherwise date the verification into the past.
  const at = Math.max(previous ?? 0, now);
  record[uid] = at;
  writeAll(record);
  return at;
}

/** When this account last held a network-verified token, or null if never seen. */
export function lastVerifiedAt(uid: string | null | undefined): number | null {
  if (!uid) return null;
  return readAll()[uid] ?? null;
}

/** §14.6.1 Is the recorded verification still inside the 48h window? */
export function isWithinGrace(
  uid: string | null | undefined,
  now: number = Date.now(),
): boolean {
  const at = lastVerifiedAt(uid);
  return at !== null && now - at < GRACE_MS;
}

/**
 * When the offline window closes, or null if it never opened.
 *
 * Word this to the user as "until **at most**": the Library's cache is evictable (§14.2.7),
 * so premium content can stop opening *before* this moment as well as at it.
 */
export function graceExpiresAt(uid: string | null | undefined): number | null {
  const at = lastVerifiedAt(uid);
  return at === null ? null : at + GRACE_MS;
}

/** Forget an account's verification — sign-out, or a deliberate reset. */
export function forgetOfflineSession(uid: string | null | undefined): void {
  if (!uid) return;
  const record = readAll();
  if (!(uid in record)) return;
  delete record[uid];
  writeAll(record);
}
